import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from "@angular/forms";
import { MatStepper } from "@angular/material";
import { ActivatedRoute } from "@angular/router";
import {
  AssessmentPageInterface,
  AssessmentQuestionGroupInterface,
  AssessmentQuestionInterface,
  EntityType,
} from "app/assessment/models";
import { SurveyInterface } from "app/assessment/survey/models";
import { SurveyService } from "app/assessment/survey/services/survey.service";
import { CreateEntityForm } from "app/assessment/utilities/create-entity-form";
import { UpdateEntity } from "app/assessment/utilities/update-entity";
import { BrowserService } from "app/core/browser/services/browser.service";
import { DashboardTabIndexEnum } from "app/my-zone/home/constants/dashboard-tab-index.enum";
import { getDeepCopyOfObject } from "app/shared/utilities/utility-methods.utility";
import { TaskInterface } from "app/task/models";
import { TaskService } from "app/task/services";
import { EMPTY, Observable, of } from "rxjs";
import { map, skip, switchMap, take } from "rxjs/operators";

@Component({
  selector: "cz-survey-stepper",
  templateUrl: "./survey-stepper.component.html",
  styleUrls: ["./survey-stepper.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyStepperComponent implements OnInit, AfterViewInit {
  @Input() public readonly survey: SurveyInterface;
  @Input() public readonly surveyStarted: boolean;
  @Input() public readonly surveyInProgress: boolean;
  @Input() public readonly surveyComplete: boolean;
  @Input() public readonly surveyImagePath: string;
  @Input() public readonly task: TaskInterface;
  @Input() public surveyProgress$: Observable<Readonly<number>>;
  @Output() public endSurvey: EventEmitter<null> = new EventEmitter<null>();
  public czSurveyFormGroup: FormGroup = new FormGroup({});
  public updatedSurvey: SurveyInterface;

  // for photo upload
  public placeholderImage: string;
  public componentName: string = EntityType.SURVEY;
  public userId: number;
  public taskId: number;

  get formArray(): AbstractControl | null {
    return this.czSurveyFormGroup.get("formArray");
  }

  @ViewChild("surveyStepper")
  public stepperComponent: MatStepper;

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _browserService: BrowserService,
    private readonly _surveyService: SurveyService,
    private readonly _taskService: TaskService,
    private readonly _formBuilder: FormBuilder,
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private _updateSurvey: UpdateEntity,
    private _createSurveyForm: CreateEntityForm,
  ) {}

  public ngOnInit(): void {
    const tId: string =
      this._route.snapshot && this._route.snapshot.queryParamMap.get("taskid");
    this.taskId = tId ? parseInt(tId, 10) : 0;

    const formArray: FormArray =
      this.survey && this._createSurveyForm.createFormArray(this.survey.pages);
    this.czSurveyFormGroup = this._formBuilder.group({ formArray });

    this.placeholderImage = `${this.surveyImagePath}section-img-upload/icon-upload.svg`;
    this.userId = this.survey && this.survey.admin.id;

    this.updatedSurvey = this.survey;
  }

  public ngAfterViewInit(): void {
    if (!this.survey) {
      return;
    }

    // To set the current page, call next() the appropriate number of times.
    for (let i: number = 0; i < this.survey.lastCompletedPage; i += 1) {
      this.stepperComponent.next();
    }

    this.updateProgress();
  }

  public stepperHeadClicked(event: StepperSelectionEvent): void {
    if (event.selectedIndex < event.previouslySelectedIndex) {
      return;
    }
    const currentGroup: any = this.formArray.get([
      event.previouslySelectedIndex,
    ]);
    this.stepperClicked(currentGroup, false, event.previouslySelectedIndex);
  }

  public stepperClicked(
    formGroup: FormGroup,
    complete: boolean,
    currentIndex: number,
  ): void {
    if (!formGroup) {
      return;
    }
    if (formGroup) {
      this.validateAllFormFields(formGroup);
    }

    // Ensure that the formGroup is valid when attempting to complete survey.
    const isComplete: boolean = complete && formGroup && formGroup.valid;
    this.updatedSurvey = this._updateSurvey.updateSurvey(
      this.updatedSurvey,
      this.stepperComponent.selectedIndex,
      this.czSurveyFormGroup,
      isComplete,
    );

    const taskId: number = this.task.id;
    this._surveyService
      .updateCurrentSurvey(this.updatedSurvey)
      .pipe(
        skip(1),
        take(1),
        switchMap((survey: SurveyInterface) => {
          if (!this.updatedSurvey.isComplete) {
            this.updateProgress(isComplete);
            return of(undefined);
          }

          return this._taskService.findOne(taskId).pipe(
            take(1),
            map((task: TaskInterface) => ({ task, survey })),
          );
        }),
        switchMap(
          (taskSurveyMap: { task: TaskInterface; survey: SurveyInterface }) => {
            if (!taskSurveyMap || !taskSurveyMap.task) {
              return of(undefined);
            }

            const taskToUpdate: TaskInterface = getDeepCopyOfObject(
              taskSurveyMap.task,
            );
            taskToUpdate.relatedEntity.entityId = taskSurveyMap.survey.id;
            if (isComplete) {
              this._taskService.close(taskToUpdate);
              this.updateProgress(isComplete);
              this.endSurvey.emit();
              return EMPTY;
            }
            return this._taskService.update(taskToUpdate).pipe(take(1));
          },
        ),
      )
      .subscribe((updatedTask: TaskInterface) => {
        if (!updatedTask) {
          return;
        }
        this._taskService.close(updatedTask);
        this.updateProgress(isComplete);
        this.endSurvey.emit();
      });

    if (formGroup && formGroup.valid) {
      // Scroll to top immediately before the stepper animation begins.
      // Using scrollTop on the actual scrollable container (mat-sidenav-content)
      // instead of scrollIntoView() which is unreliable in Safari/iPadOS
      // within nested scroll containers.
      this.scrollToTop();
    } else if (formGroup && !formGroup.valid) {
      setTimeout(() => {
        const errorElement: Element = this._document.querySelector(
          "#czSurveyStepperForm .error-msg",
        );
        if (!errorElement) {
          return;
        }
        const scrollContainer: Element = this._document.querySelector(
          "mat-sidenav-content",
        );
        if (scrollContainer) {
          const errorRect = errorElement.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const offset: number =
            errorRect.top - containerRect.top + scrollContainer.scrollTop;
          scrollContainer.scrollTo({
            top: offset - scrollContainer.clientHeight / 2,
            behavior: "smooth",
          });
        } else {
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
        this.stepperComponent.selectedIndex = currentIndex;
        this.stepperComponent.selected =
          this.stepperComponent._steps[currentIndex];
      });
    }
  }

  public continueToRecommendations(): void {
    this._browserService.toDashboard(DashboardTabIndexEnum.recommendations);
  }

  private scrollToTop(): void {
    const scrollContainer: Element = this._document.querySelector(
      "mat-sidenav-content",
    );
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    } else {
      this._document.querySelector(".page-view-content").scrollIntoView();
    }
  }

  private updateProgress(isComplete?: boolean): void {
    if (isComplete) {
      this.surveyProgress$ = this._surveyService.updateSurveyProgress(null);
    } else {
      const progress: number = this.calculateProgressPercentage(
        this.stepperComponent.selectedIndex,
        this.survey,
      );
      this.surveyProgress$ = this._surveyService.updateSurveyProgress(progress);
    }
  }

  private calculateProgressPercentage(
    step: number,
    survey: SurveyInterface,
  ): number {
    let questionsPerPage: number[] = [];
    const surveyPages: AssessmentPageInterface[] = survey.pages;
    const reducer: any = (accumulator: number, currentValue: number): number =>
      accumulator + currentValue;

    for (const pageKey in surveyPages) {
      if (!surveyPages.hasOwnProperty(pageKey)) {
        continue;
      }
      const qGroups: AssessmentQuestionGroupInterface[] =
        surveyPages[pageKey].questionGroups;

      questionsPerPage = this.getQuestionsPerPage(
        pageKey,
        questionsPerPage,
        qGroups,
      );
    }

    let percentDone: number = 0;
    for (let i: number = 0; i <= step - 1; i += 1) {
      percentDone +=
        (questionsPerPage[i] / questionsPerPage.reduce(reducer)) * 100;
    }

    return Math.round(percentDone);
  }

  private getQuestionsPerPage(
    pageKey: string,
    questionsPerPage: number[],
    qGroups: AssessmentQuestionGroupInterface[],
  ): number[] {
    questionsPerPage[pageKey] = 0;

    for (const qGroupKey in qGroups) {
      if (!qGroups.hasOwnProperty(qGroupKey)) {
        continue;
      }
      const questions: AssessmentQuestionInterface[] =
        qGroups[qGroupKey].questions;
      questionsPerPage[pageKey] += questions.length;
    }

    return questionsPerPage;
  }

  private validateAllFormFields(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field: any) => {
      const control: AbstractControl = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }
}
